'use client';

import {
  createContext,
  useContext,
  useState,
  useLayoutEffect,
  useMemo,
} from 'react';
import { useBannerHeightSync } from './use-banner-height-sync';
import {
  BANNER_HIDDEN_STORAGE_KEY,
  BANNER_HOOK,
  getVisibleBannerIds,
  markBannerReady,
  readAndPruneHiddenUntilById,
  readHiddenUntilById,
  type BannerRule,
  type HiddenUntilById,
} from './banner-eligibility';

const DEFAULT_HIDE_DURATION = 24; // 배너 숨기기 기간: 기본 하루

export interface BannerContextType {
  /** 노출 중 배너 중 숨기지 않은 것의 id (서버가 준 순서 유지) */
  visibleIds: string[];
  /** 노출 중 배너 중 하나라도 "24시간 닫기"를 허용하면 true — 닫기는 캐러셀 전체를 숨긴다 */
  canHide: boolean;
  /** 판정이 끝나 React 가 배너를 소유했는지 — 그 전엔 인계 CSS 가 보임·숨김을 맡는다 */
  isReady: boolean;
  /** 지금 보이는 배너 전부를 숨긴다 */
  hideBanner: (durationInHours?: number) => void;
}
const BannerContext = createContext<BannerContextType | null>(null);

export function BannerProvider({
  rules,
  children,
}: {
  /** 서버가 렌더한 배너들의 일정·닫기 허용 여부 (이미 종료된 배너는 빠져 있음) */
  rules: BannerRule[];
  children: React.ReactNode;
}) {
  // 하이드레이션은 서버와 같은 트리(전 배너)로 한다 — 판정 전 화면은 인라인 스크립트 +
  // 인계 CSS 가 이미 맞춰 그려 두었다. 판정은 아래 레이아웃 이펙트에서 한다.
  const [visibleIds, setVisibleIds] = useState(() => rules.map(r => r.id));
  const [isReady, setIsReady] = useState(false);
  const isBannerVisible = visibleIds.length > 0;
  // 로케일 전환처럼 배너 목록이 바뀌면 다시 판정한다 (헤더는 라우트 간 유지돼 재마운트되지 않음)
  const rulesKey = rules
    .map(r => `${r.id}:${r.openedAt}:${r.closedAt}`)
    .join('|');

  useLayoutEffect(() => {
    // 저장소 정리와 판정이 같은 시각을 기준으로 하도록 한 번만 잰다
    const now = Date.now();
    let hiddenUntilById: HiddenUntilById = {};
    try {
      hiddenUntilById = readAndPruneHiddenUntilById(now);
    } catch (error) {
      // 저장소를 못 읽으면 "닫지 않음"으로 보고 일정 판정은 계속한다 (스크립트와 같은 정책)
      console.error('Failed to access localStorage:', error);
    }

    setVisibleIds(getVisibleBannerIds(rules, hiddenUntilById, now));
    setIsReady(true);
    // 레이아웃 이펙트 안의 상태 변경은 페인트 전에 동기로 커밋되므로 그 사이 프레임이 없고,
    // 두 표현의 레이아웃이 같아 소유권이 넘어가도 화면이 움직이지 않는다.
    markBannerReady();
    // rules 는 rulesKey 로 비교한다 — 서버 컴포넌트가 매 렌더 새 배열을 넘긴다
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rulesKey]);

  const canHide = rules.some(
    rule => rule.closable && visibleIds.includes(rule.id),
  );

  const contextValue = useMemo(
    () => ({
      visibleIds,
      canHide,
      isReady,
      hideBanner: (durationInHours = DEFAULT_HIDE_DURATION) => {
        if (visibleIds.length === 0) return;

        // 화면은 먼저 숨긴다. 저장에 실패하면 이번 화면에서만 숨겨지고 다음 방문엔 다시 보인다.
        setVisibleIds([]);

        try {
          const hiddenUntilById = readHiddenUntilById();
          const until = Date.now() + durationInHours * 60 * 60 * 1000;
          // 캐러셀 전체를 닫는다 — 지금 보이는 배너 모두를 같은 기한으로 기록
          for (const id of visibleIds) hiddenUntilById[id] = until;

          localStorage.setItem(
            BANNER_HIDDEN_STORAGE_KEY,
            JSON.stringify(hiddenUntilById),
          );
        } catch (error) {
          console.error('Failed to hide banner:', error);
        }
      },
    }),
    [visibleIds, canHide, isReady],
  );

  const bannerRef = useBannerHeightSync(isBannerVisible);

  return (
    <BannerContext.Provider value={contextValue}>
      <div ref={bannerRef} {...{ [BANNER_HOOK.ROOT]: '' }}>
        {isBannerVisible && children}
      </div>
    </BannerContext.Provider>
  );
}

export function useBanner() {
  const context = useContext(BannerContext);
  if (!context) {
    throw new Error('useBanner must be used within BannerProvider');
  }
  return context;
}
